import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFreemiumCheckoutStore } from '../stores/freemium-checkout-store';
import { useFreemiumUrlState } from './use-freemium-url-state';

import { freemiumApi, freemiumKeys } from '@client/api/waterlily';
import { API } from '@/api/types';

import type { FreemiumTier } from '@shared';

interface SelectionVariables {
  tier?: FreemiumTier;
  details?: string;
}

export function useFreemiumSelection(clientId: string) {
  const queryClient = useQueryClient();
  const { intent, reflexiveAnswer, startCheckout, goToHandoffDone } = useFreemiumUrlState();
  const setClientSecret = useFreemiumCheckoutStore(state => state.setClientSecret);

  // ?freemiumPaid=1 lets the destination poll for the unlock (which arrives via webhook
  // a moment after payment) and show a "finalizing" state, then the paid success screen.
  // The fx* params give that success screen the tier/intent context it needs. Self-funding's
  // paywall is reached from the quotes-selection gate inside guided planning, so return there.
  function buildReturnPath(tier?: FreemiumTier) {
    const base =
      intent === 'self-funding'
        ? `/clients/${clientId}/onboarding/guided-planning/quotes-selection`
        : `/clients/${clientId}/onboarding/funding-sources`;
    const params = new URLSearchParams({ freemiumPaid: '1' });

    if (tier) params.set('fxTier', tier);
    if (intent) params.set('fxIntent', intent);
    if (reflexiveAnswer) params.set('fxAnswer', reflexiveAnswer);

    return `${base}?${params.toString()}`;
  }

  const mutation = useMutation({
    mutationFn: ({ tier, details }: SelectionVariables) =>
      freemiumApi.postSelection(clientId, {
        intent: intent ?? '',
        tier,
        reflexiveAnswer: reflexiveAnswer ?? undefined,
        details,
        returnPath: buildReturnPath(tier),
        uiMode: 'embedded',
      }),
    onSuccess: (result: API.FreemiumSelectionResponse, variables: SelectionVariables) => {
      if (result.requiresPayment && result.clientSecret && variables.tier) {
        setClientSecret(result.clientSecret);
        startCheckout(variables.tier);
        return;
      }

      queryClient.invalidateQueries({ queryKey: freemiumKeys.planningUnlock(clientId) });

      if (result.alreadyUnlocked) return;

      goToHandoffDone();
    },
  });

  function selectTier(tier: FreemiumTier) {
    mutation.mutate({ tier });
  }

  function submitConsult(details: string) {
    mutation.mutate({ details });
  }

  function recreateCheckout(tier: FreemiumTier) {
    mutation.mutate({ tier });
  }

  return {
    selectTier,
    submitConsult,
    recreateCheckout,
    isPending: mutation.isPending,
    pendingTier: mutation.variables?.tier,
  };
}
